import { useState } from "react";
import { Avatar } from "@/ui/Avatar/Avatar";
import { Tag } from "./Tag";
import { BrowserSize, BrowserWrapper } from "@/Apps/Pages/Editor/BrowserWrapper";
import { MobileState } from "@/Apps/Pages/MobileResponsiveToggle/types";
import { PopupSizes } from "@/ui/Modal/types";
import theme from "@/ui/theme";
import { Browsers, NewspaperClipping, TreeStructure } from "@phosphor-icons/react";

import { LAUNCH_OS_ICON } from "@/brand";
export enum CardTypes {
  APP = "app",
  PAGE = "page",
  CHAT_SESSION = "chat_session",
}

interface CardProps {
  title?: string;
  backgroundImage: string;
  backgroundVideo?: string;
  price: number;
  description?: string;
  className?: string;
  customStyles?: Record<string, string>;
  tags?: string[];
  onClick?: () => void;
  children?: React.ReactNode;
  type?: CardTypes;
  size?: PopupSizes;
  dataTestId?: string;
}

export const AppThumbnail: React.FC<CardProps> = ({
  backgroundImage,
  backgroundVideo,
  title,
  size = PopupSizes.SMALL,
}) => {
  return (
    <div
      // style={{
      //   background: `linear-gradient(to bottom right, ${
      //     ["#4F46E5", "#3B82F6", "#6366F1", "#EC4899", "#F43F5E"][
      //       Math.abs(title?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0) % 5
      //     ]
      //   }, ${
      //     ["#3B82F6", "#4F46E5", "#EC4899", "#F43F5E", "#6366F1"][
      //       Math.abs((title?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0) + 1) % 5
      //     ]
      //   })`,
      // }}
      style={{
        background: `linear-gradient(to bottom right, ${
          [theme.primaryColor, "#4F46E5", theme.secondaryColor, theme.tertiaryColor, ...theme.altColors][
            Math.abs(title?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0) % 11
          ]
        }, ${
          [theme.primaryColor, "#3B82F6", theme.secondaryColor, theme.tertiaryColor, ...theme.altColors][
            Math.abs(title?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0) % 11
          ]}88)`,
      }}
      className={`transition-colors duration-300`}
    >
      <h3 className={`text-lg text-white font-bold translate-x-7 pt-4 ${size === PopupSizes.LARGE ? "pb-8" : "pb-3"}`}>{size === PopupSizes.LARGE ? " " : title}</h3>
      <div className="overflow-hidden">
        <div
          className="object-cover w-full h-full transform translate-x-7
          shadow-xl"
        >
          <BrowserWrapper
            mobileState={{ type: MobileState.DESKTOP }}
            withUrl={true}
            withPreview={false}
            withUndoRedo={false}
            withFullScreen={false}
            withCustomButtons={false}
            size={size === PopupSizes.LARGE ? BrowserSize.MEDIUM : BrowserSize.SMALL}
          >
            {backgroundVideo ? (
              <video src={backgroundVideo} autoPlay muted loop />
            ) : (
              <img src={backgroundImage} alt={title} />
            )}
          </BrowserWrapper>
        </div>
      </div>
    </div>
  );
};

export const AppCard: React.FC<CardProps> = ({
  title = "by Ten Minute Funnels",
  backgroundImage,
  backgroundVideo,
  price,
  description = "A simple landing page for a SaaS product.",
  className = "",
  customStyles = {},
  tags = [],
  onClick = () => null,
  children,
  size = PopupSizes.SMALL,
  // type = CardTypes.PAGE,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer dark:shadow-3xl dark:bg-white/5 rounded-lg overflow-hidden mb-3 border border-black/10 dark:border-white/10 ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="overflow-hidden" style={{ maxHeight: 220 }}>
        <AppThumbnail
          backgroundImage={backgroundImage}
          backgroundVideo={backgroundVideo}
          title={title}
          size={size}
        />
      </div>
      <div className="p-4">
        <Avatar
          type="Text"
          src={LAUNCH_OS_ICON}
          label={title}
          caption={description}
        />
      </div>
    </div>
  );
};

export const PageCard: React.FC<CardProps> = ({
  title = "by Launch OS",
  backgroundImage,
  price,
  description = "A simple landing page for a SaaS product.",
  className = "",
  customStyles = {},
  tags = [],
  onClick = () => null,
  children,
  type = CardTypes.PAGE,
  dataTestId = "",
  }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const cardStyle = {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
    minHeight: "180px",
    backgroundPosition: isHovered ? "center bottom" : "top",
    marginTop: isHovered ? "-5px" : "0px",
    backgroundColor: "transparent",
    ...customStyles,
  };

  return (
    <div className="mb-7" onClick={onClick}>
      <div
        data-testid={dataTestId}
        className={`bg-white cursor-pointer dark:shadow-3xl dark:bg-white/5 rounded-2xl p-4 mb-3 border border-black/20 dark:border-white/10 ${className}`}
        style={cardStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {price && <Tag key="price">{price}</Tag>}

        {tags.map((tag, key) => (
          <Tag key={key}>{tag.toUpperCase()}</Tag>
        ))}

        {children}
      </div>
      <Avatar
        type="Text"
        src={LAUNCH_OS_ICON}
        label={title}
        caption={description}
      />
    </div>
  );
};

export const ChatSessionCard: React.FC<CardProps> = ({
  title = "by Launch OS",
  backgroundImage,
  price,
  description = "A simple landing page for a SaaS product.",
  className = "",
  customStyles = {},
  tags = [],
  onClick = () => null,
  children,
  size = PopupSizes.SMALL,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer dark:shadow-3xl dark:bg-white/5 rounded-lg overflow-hidden mb-3 border border-black/10 dark:border-white/10 flex flex-col justify-between ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="overflow-hidden relative">
        <div className="absolute top-0 right-0 z-0 pointer-events-none select-none" style={{padding: 16}}>
          <span
            style={{
              fontSize: 96,
              // color: "rgba(0,0,0,0.07)",
              // color: "white",
              fontWeight: 900,
              lineHeight: 1,
              userSelect: "none",
              textShadow: "0 2px 8px rgba(0,0,0,0.04)",
              fontFamily: "serif",
              opacity: 0.5,
            }}
            className="dark:text-white/10 text-black/10"
          >
            &rdquo;
          </span>
        </div>
        <div
          className={`transition-colors duration-300 h-full min-h-[120px] flex`}
          style={{ minHeight: 120 }}
        >
          <div className="p-6 h-full flex flex-col text-black/80 dark:text-white/80">
            <div className="text-left pr-10">
              {title}
              {/* <h3 className="text-white/90 font-medium text-sm leading-tight">
              </h3> */}
              {/* {description && (
                <p className="text-white/70 text-xs mt-1 leading-tight">
                  {description}
                </p>
              )} */}
            </div>
          </div>

          {/* Hover Overlay */}
          {isHovered && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center transition-all duration-300">
              <button className="bg-white text-black px-4 py-2.5 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 shadow-lg flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                View replay
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="p-6">
        {tags.includes("page") && (
          <div className="flex gap-2">
            <div className={`rounded p-1 bg-green-500`}>
              <Browsers />
            </div>
            <span className="text-md text-black/50 dark:text-white/50">Launch Pages</span>
          </div>
        )}
        {tags.includes("funnel") && (
          <div className="flex gap-2">
            <div className={`rounded p-1 bg-yellow-500`}>
              <TreeStructure />
            </div>
            <span className="text-md text-black/50 dark:text-white/50">Launch Funnels</span>
          </div>
        )}
        {tags.includes("content") && (
          <div className="flex gap-2">
            <div className={`rounded p-1 bg-cyan-500`}>
              <NewspaperClipping />
            </div>
            <span className="text-md text-black/50 dark:text-white/50">Launch Content</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const Card: React.FC<CardProps> = props => {
  if (props.type === CardTypes.APP) {
    return <AppCard {...props} />;
  }

  if (props.type === CardTypes.CHAT_SESSION) {
    return <ChatSessionCard {...props} />;
  }

  return <PageCard {...props} />;
};
